/**
 * Scooter Pack Service for Fleet Riders
 * Handles scooter pack operations for fleet users
 */

import apiClient from './apiClient';
import { normalizePackPurchaseResponse, type RawPackPurchaseResponse } from './purchaseResponse';
import type { PackPurchaseResponse } from './types';

// Types
export interface ScooterPackTemplate {
  template_id: string;
  template_name: string;
  description?: string;
  pack_type: 'hybrid' | 'distance_only' | 'time_only';
  price_npr: number;
  validity_hours?: number;
  distance_limit_km?: number;
  company_name?: string;
  is_active: boolean;
  priority?: number;
}

export interface ScooterPack {
  pack_id: string;
  template_id: string;
  template_name?: string;
  user_id?: string;
  scooter_vin: string;
  status: 'reserved' | 'queued' | 'active' | 'expired' | 'depleted' | 'consumed';
  pack_type: 'hybrid' | 'distance_only' | 'time_only';
  price_npr: number;
  validity_hours?: number;
  distance_limit_km?: number;
  distance_used_km?: number;
  activation_date?: string;
  expiry_date?: string;
  designated_next?: boolean;
  created_at: string;
  updated_at?: string;
}

export interface ScooterPackEligibility {
  eligible: boolean;
  reasons: string[];
  available_templates: ScooterPackTemplate[];
  wallet_balance?: number;
  has_active_pack: boolean;
  active_pack_expires?: string;
}

export interface ScooterPackPurchaseRequest {
  template_id: string;
  scooter_vin: string;
}

export type ScooterPackPurchaseResponse = PackPurchaseResponse;

class ScooterPackService {
  /**
   * Get available scooter pack templates for the user
   */
  async getTemplates(): Promise<ScooterPackTemplate[]> {
    console.log('📦 ScooterPackService: Getting templates');

    try {
      const response = await apiClient.get<ScooterPackTemplate[]>('/api/v1/scooter-packs/templates');
      console.log('📦 ScooterPackService: Templates response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ ScooterPackService: Failed to get templates:', error);
      throw error;
    }
  }

  /**
   * Check eligibility to purchase a scooter pack for a specific scooter
   */
  async checkEligibility(scooterVin: string): Promise<ScooterPackEligibility> {
    console.log('📦 ScooterPackService: Checking eligibility for VIN:', scooterVin);

    try {
      const response = await apiClient.get<ScooterPackEligibility>(
        `/api/v1/scooter-packs/eligibility/${scooterVin}`
      );
      console.log('📦 ScooterPackService: Eligibility response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ ScooterPackService: Eligibility check failed:', error);
      throw error;
    }
  }

  /**
   * Purchase a scooter pack
   */
  async purchasePack(request: ScooterPackPurchaseRequest): Promise<ScooterPackPurchaseResponse> {
    console.log('📦 ScooterPackService: Purchasing pack:', request);

    try {
      const response = await apiClient.post<RawPackPurchaseResponse>(
        '/api/v1/scooter-packs/purchase',
        request
      );
      console.log('📦 ScooterPackService: Purchase response:', response.data);
      return normalizePackPurchaseResponse(response.data);
    } catch (error: any) {
      console.error('❌ ScooterPackService: Purchase failed:', error);
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw error;
    }
  }

  /**
   * Get user's scooter packs
   */
  async getUserPacks(status?: string, scooterVin?: string): Promise<{
    packs: ScooterPack[];
    total: number;
  }> {
    console.log('📦 ScooterPackService: Getting user packs');

    try {
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      if (scooterVin) params.append('scooter_vin', scooterVin);

      const queryString = params.toString();
      const url = `/api/v1/scooter-packs${queryString ? `?${queryString}` : ''}`;

      const response = await apiClient.get<{ packs: ScooterPack[]; total: number }>(url);
      console.log('📦 ScooterPackService: User packs response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ ScooterPackService: Failed to get user packs:', error);
      throw error;
    }
  }

  /**
   * Get active scooter pack for a specific scooter
   */
  async getActivePack(scooterVin: string): Promise<ScooterPack | null> {
    console.log('📦 ScooterPackService: Getting active pack for VIN:', scooterVin);

    try {
      const response = await apiClient.get<{ active_pack: ScooterPack | null }>(
        `/api/v1/scooter-packs/active/${scooterVin}`
      );
      console.log('📦 ScooterPackService: Active pack response:', response.data);
      return response.data.active_pack;
    } catch (error) {
      console.error('❌ ScooterPackService: Failed to get active pack:', error);
      return null;
    }
  }

  /**
   * Manually activate a reserved scooter pack
   */
  async activatePack(packId: string): Promise<ScooterPack> {
    console.log('📦 ScooterPackService: Activating pack:', packId);

    try {
      const response = await apiClient.post<{ pack: ScooterPack }>(
        `/api/v1/scooter-packs/${packId}/activate`
      );
      console.log('📦 ScooterPackService: Activation response:', response.data);
      return response.data.pack;
    } catch (error: any) {
      console.error('❌ ScooterPackService: Activation failed:', error);
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw error;
    }
  }

  /**
   * Designate a reserved pack as next to activate
   */
  async designateNextPack(packId: string): Promise<ScooterPack> {
    console.log('📦 ScooterPackService: Designating pack as next:', packId);

    try {
      const response = await apiClient.post<{ pack: ScooterPack }>(
        `/api/v1/scooter-packs/${packId}/designate-next`
      );
      console.log('📦 ScooterPackService: Designation response:', response.data);
      return response.data.pack;
    } catch (error: any) {
      console.error('❌ ScooterPackService: Designation failed:', error);
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw error;
    }
  }

  /**
   * Get pack details
   */
  async getPackDetails(packId: string): Promise<{
    pack: ScooterPack;
    template?: ScooterPackTemplate;
    warnings: string[];
  }> {
    console.log('📦 ScooterPackService: Getting pack details:', packId);

    try {
      const response = await apiClient.get<{
        pack: ScooterPack;
        template?: ScooterPackTemplate;
        warnings: string[];
      }>(`/api/v1/scooter-packs/${packId}`);
      console.log('📦 ScooterPackService: Pack details response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ ScooterPackService: Failed to get pack details:', error);
      throw error;
    }
  }

  /**
   * Calculate remaining values for display
   */
  calculatePackStatus(pack: ScooterPack): {
    distanceRemaining: number | null;
    hoursRemaining: number | null;
    percentRemaining: number;
    statusColor: 'green' | 'orange' | 'red';
    isExpiringSoon: boolean;
  } {
    let distanceRemaining: number | null = null;
    let hoursRemaining: number | null = null;

    // Calculate distance remaining
    if (pack.distance_limit_km) {
      const used = pack.distance_used_km || 0;
      distanceRemaining = Math.max(0, pack.distance_limit_km - used);
    }

    // Calculate hours remaining
    if (pack.expiry_date) {
      const now = new Date();
      const expiry = new Date(pack.expiry_date);
      hoursRemaining = Math.max(0, (expiry.getTime() - now.getTime()) / (1000 * 60 * 60));
    }

    // Calculate percentage remaining (minimum of both if applicable)
    let percentRemaining = 100;

    if (pack.distance_limit_km && distanceRemaining !== null) {
      percentRemaining = Math.min(percentRemaining, (distanceRemaining / pack.distance_limit_km) * 100);
    }

    if (pack.validity_hours && hoursRemaining !== null) {
      percentRemaining = Math.min(percentRemaining, (hoursRemaining / pack.validity_hours) * 100);
    }

    // Determine status color
    let statusColor: 'green' | 'orange' | 'red' = 'green';
    if (percentRemaining <= 20) {
      statusColor = 'red';
    } else if (percentRemaining <= 50) {
      statusColor = 'orange';
    }

    // Check if expiring soon (within 24 hours)
    const isExpiringSoon = hoursRemaining !== null && hoursRemaining < 24;

    return {
      distanceRemaining,
      hoursRemaining,
      percentRemaining,
      statusColor,
      isExpiringSoon,
    };
  }
}

export const scooterPackService = new ScooterPackService();
export default scooterPackService;
